import { useState, useEffect } from 'react';
import { useStore } from '../store.js';

const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC','PR'];

const empty = {
  first_name: '', last_name: '', ownership_pct: '',
  email: '', phone: '',
  ssn: '', dob: '',
  address_line1: '', address_line2: '', city: '', state: '', zip: '',
};

const emptyAdd = { first_name: '', last_name: '', ownership_pct: '', email: '', phone: '' };

function validateDraft(draft) {
  const errors = {};
  if (!draft.first_name?.trim()) errors.first_name = 'Required.';
  if (!draft.last_name?.trim())  errors.last_name  = 'Required.';

  const pct = Number(draft.ownership_pct);
  if (!pct || pct <= 0 || pct > 100) errors.ownership_pct = 'Must be 1–100.';

  const ssnDigits = (draft.ssn || '').replace(/\D/g, '');
  if (ssnDigits.length !== 9) errors.ssn = 'Must be 9 digits.';

  if (!draft.dob) {
    errors.dob = 'Required.';
  } else {
    const dob = new Date(draft.dob);
    if (isNaN(dob.getTime())) errors.dob = 'Invalid date.';
    else {
      const age = (Date.now() - dob.getTime()) / (365.25 * 24 * 3600 * 1000);
      if (age < 18) errors.dob = 'Owner must be at least 18.';
      else if (age > 110) errors.dob = 'Date looks incorrect.';
    }
  }

  if (!draft.address_line1?.trim()) errors.address_line1 = 'Required.';
  if (!draft.city?.trim())          errors.city          = 'Required.';
  if (!draft.state?.trim())         errors.state         = 'Required.';
  const zip = (draft.zip || '').replace(/\D/g, '').slice(0, 5);
  if (!/^\d{5}$/.test(zip))         errors.zip           = 'Must be 5 digits.';

  return errors;
}

// The added-owner mini form only collects name + ownership% + email/phone.
// That owner will fill their own personal details from their own signing link.
function validateAddDraft(draft) {
  const errors = {};
  if (!draft.first_name?.trim()) errors.first_name = 'Required.';
  if (!draft.last_name?.trim())  errors.last_name  = 'Required.';
  const pct = Number(draft.ownership_pct);
  if (!pct || pct <= 0 || pct > 100) errors.ownership_pct = 'Must be 1–100.';
  return errors;
}

export function Owners({ onNext, onBack }) {
  const owners = useStore((s) => s.owners);
  const addOwner = useStore((s) => s.addOwner);
  const removeOwner = useStore((s) => s.removeOwner);
  const updateOwner = useStore((s) => s.updateOwner);

  // The primary owner is index 0 — the person filling out this application.
  const sorted = [...owners].sort((a, b) => (a.owner_index ?? 0) - (b.owner_index ?? 0));
  const primary = sorted[0] || null;
  const others = sorted.slice(1);

  const [draft, setDraft] = useState(empty);
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Add-owner mini form (name + ownership% + email/phone only).
  const [addOpen, setAddOpen] = useState(false);
  const [addDraft, setAddDraft] = useState(emptyAdd);
  const [addErrors, setAddErrors] = useState({});
  const [addBusy, setAddBusy] = useState(false);

  // Seed the editable form from the primary owner once it's available.
  useEffect(() => {
    if (primary) {
      setDraft({ ...empty, ...primary, ownership_pct: primary.ownership_pct ?? '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primary?.id]);

  const totalPct = owners.reduce((a, o) => a + Number(o.ownership_pct || 0), 0);

  function patch(k, v) {
    setDraft((d) => ({ ...d, [k]: v }));
    if (errors[k]) setErrors((e) => { const { [k]: _, ...rest } = e; return rest; });
  }

  function patchAdd(k, v) {
    setAddDraft((d) => ({ ...d, [k]: v }));
    if (addErrors[k]) setAddErrors((e) => { const { [k]: _, ...rest } = e; return rest; });
  }

  // Save the primary owner's own personal info (index 0).
  async function commitPrimary() {
    setGlobalError(null);
    const errs = validateDraft(draft);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return false;
    }
    setErrors({});
    const body = { ...draft, ownership_pct: Number(draft.ownership_pct) };
    setBusy(true);
    try {
      if (primary) await updateOwner(primary.id, body);
      else await addOwner(body);
      return true;
    } catch (e) {
      setGlobalError(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  }

  // Add another owner — name + ownership% + email/phone only. No SSN/DOB/address.
  async function commitAddOwner() {
    setGlobalError(null);
    const errs = validateAddDraft(addDraft);
    if (Object.keys(errs).length > 0) {
      setAddErrors(errs);
      return false;
    }
    setAddErrors({});
    const body = {
      first_name: addDraft.first_name,
      last_name: addDraft.last_name,
      ownership_pct: Number(addDraft.ownership_pct),
      email: addDraft.email || null,
      phone: addDraft.phone || null,
    };
    setAddBusy(true);
    try {
      await addOwner(body);
      setAddDraft(emptyAdd);
      setAddOpen(false);
      return true;
    } catch (e) {
      setGlobalError(e.message);
      return false;
    } finally {
      setAddBusy(false);
    }
  }

  async function handleContinue() {
    setGlobalError(null);
    // Unlike the rest of the form, the add-owner card doesn't autosave — a
    // filled-but-uncommitted draft here would be silently lost on Continue,
    // so save (or block on) it before moving on.
    const addDraftHasContent = Object.values(addDraft).some((v) => String(v ?? '').trim() !== '');
    if (addOpen && addDraftHasContent) {
      const added = await commitAddOwner();
      if (!added) {
        setGlobalError((prev) => prev || 'Please finish adding the other owner (tap "Add owner") or clear the form before continuing.');
        return;
      }
    }
    const ok = await commitPrimary();
    if (!ok) return;
    const fresh = useStore.getState().owners;
    if (fresh.length === 0) {
      setGlobalError('Please add at least one owner before continuing.');
      return;
    }
    onNext();
  }

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-xl font-semibold text-brand-900">Owners</h2>
        <p className="text-sm text-slate-500">Confirm your own information below. Add every other individual who owns 20%+ of the business — each will receive their own link to review and sign their information.</p>
      </header>

      {/* Primary owner (index 0): editable personal info. */}
      <div className="card p-4 space-y-4">
        <h3 className="font-medium text-slate-800">Your information</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="First name" req error={errors.first_name} v={draft.first_name} on={(v) => patch('first_name', v)} />
          <Field label="Last name"  req error={errors.last_name}  v={draft.last_name}  on={(v) => patch('last_name', v)} />
          <Field label="Ownership %" req type="number" error={errors.ownership_pct} v={draft.ownership_pct} on={(v) => patch('ownership_pct', v)} />
          <Field label="Date of birth" req type="date" error={errors.dob} v={draft.dob} on={(v) => patch('dob', v)} />
          <Field label="SSN" req type="text" placeholder="XXX-XX-XXXX" error={errors.ssn} v={draft.ssn} on={(v) => patch('ssn', v)} />
          <Field label="Email" type="email" v={draft.email} on={(v) => patch('email', v)} />
          <Field label="Phone" type="tel" v={draft.phone} on={(v) => patch('phone', v)} />
          <div />
          <Field label="Street" req error={errors.address_line1} v={draft.address_line1} on={(v) => patch('address_line1', v)} />
          <Field label="Suite / unit" v={draft.address_line2} on={(v) => patch('address_line2', v)} />
          <Field label="City" req error={errors.city} v={draft.city} on={(v) => patch('city', v)} />
          <Select label="State" req error={errors.state} v={draft.state} on={(v) => patch('state', v)} options={STATES} />
          <Field label="ZIP" req error={errors.zip} v={draft.zip} on={(v) => patch('zip', v)} />
        </div>
      </div>

      {/* Other owners (index >= 1): read-only summary. They complete their own info. */}
      {others.length > 0 && (
        <div className="card divide-y divide-slate-200">
          {others.map((o) => (
            <div key={o.id} className="px-4 py-3 flex items-center justify-between">
              <div>
                <p className="font-medium">{o.first_name} {o.last_name}</p>
                <p className="text-xs text-slate-500">
                  {o.ownership_pct ? `${o.ownership_pct}% · ` : ''}{o.email || 'no email'}
                </p>
                <p className="text-xs text-slate-500 mt-1">This owner will receive their own link to review and sign their information.</p>
              </div>
              <button className="text-red-600 text-sm hover:underline" onClick={() => removeOwner(o.id)}>Remove</button>
            </div>
          ))}
          <div className="px-4 py-2 text-xs text-slate-500 bg-slate-50">
            Total ownership: <strong>{totalPct}%</strong>
            {totalPct > 100 && <span className="text-red-600 ml-2">(over 100% — please adjust)</span>}
          </div>
        </div>
      )}

      {/* Add another owner — name + ownership% + email/phone only. */}
      {addOpen ? (
        <div className="card p-4 space-y-4">
          <h3 className="font-medium text-slate-800">Add another owner</h3>
          <p className="text-xs text-slate-500">Enter their name and contact details. They’ll fill in the rest of their own information when they receive their signing link.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="First name" req error={addErrors.first_name} v={addDraft.first_name} on={(v) => patchAdd('first_name', v)} />
            <Field label="Last name"  req error={addErrors.last_name}  v={addDraft.last_name}  on={(v) => patchAdd('last_name', v)} />
            <Field label="Ownership %" req type="number" error={addErrors.ownership_pct} v={addDraft.ownership_pct} on={(v) => patchAdd('ownership_pct', v)} />
            <div />
            <Field label="Email" type="email" v={addDraft.email} on={(v) => patchAdd('email', v)} />
            <Field label="Phone" type="tel" v={addDraft.phone} on={(v) => patchAdd('phone', v)} />
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <button className="btn-primary" onClick={commitAddOwner} disabled={addBusy}>
              {addBusy ? 'Saving…' : 'Add owner'}
            </button>
            <button className="btn-ghost" onClick={() => { setAddDraft(emptyAdd); setAddErrors({}); setAddOpen(false); }}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className="btn-ghost" onClick={() => { setGlobalError(null); setAddOpen(true); }}>+ Add owner</button>
      )}

      {globalError && <p className="field-error">{globalError}</p>}

      <div className="flex justify-between items-center">
        <button className="btn-ghost" onClick={onBack}>Back</button>
        <button className="btn-primary" onClick={handleContinue} disabled={busy}>
          {busy ? 'Saving…' : 'Continue'}
        </button>
      </div>
    </div>
  );
}

function Field({ label, type = 'text', v, on, req, error, placeholder }) {
  return (
    <label className="block">
      <span className="field-label">{label}{req && <span className="text-red-500"> *</span>}</span>
      <input
        type={type}
        className={`field-input ${error ? 'is-error' : ''}`}
        aria-invalid={!!error}
        value={v ?? ''}
        placeholder={placeholder}
        onChange={(e) => on(e.target.value)}
      />
      {error && <p className="field-error">{error}</p>}
    </label>
  );
}

function Select({ label, v, on, options, req, error }) {
  return (
    <label className="block">
      <span className="field-label">{label}{req && <span className="text-red-500"> *</span>}</span>
      <select
        className={`field-select ${error ? 'is-error' : ''}`}
        aria-invalid={!!error}
        value={v ?? ''}
        onChange={(e) => on(e.target.value)}
      >
        <option value="">— select —</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      {error && <p className="field-error">{error}</p>}
    </label>
  );
}
